import { useCallback, useEffect, useRef, useState } from 'react';

// One press advances exactly one item: the offset between the first two items, which is the
// item's rendered width plus the row's gap. A fraction of the container only *looked* right
// on desktop, where the items happen to be 40% of the row; on a phone the items are full
// width, so a third of the row left two thirds of the next card off screen.
//
// Reading that offset, rather than `width + parseFloat(columnGap)`, is what lets one row with
// a gap and one without share the measurement. The video-tutorial rows are `gap-x-6`; the home
// pricing table separates its columns with borders, so its `column-gap` computes to `normal`
// and `parseFloat('normal')` is NaN -- and `scrollTo({ left: NaN })` is not a no-op: the
// browser normalizes the non-finite value to 0, so the row visibly jumps back to its start.
//
// `''` comes back only for an element that is not in the document. A single item has no second
// offset to read, so its own width is the step; a row of one cannot overflow, so that value is
// never consumed for paging.
export const getItemStepPx = (row: HTMLElement): number => {
  const firstItem = row.firstElementChild as HTMLElement | null;
  if (!firstItem) return row.clientWidth;

  const firstLeft = firstItem.getBoundingClientRect().left;
  const secondItem = firstItem.nextElementSibling as HTMLElement | null;
  if (!secondItem) return firstItem.getBoundingClientRect().width;

  return secondItem.getBoundingClientRect().left - firstLeft;
};

// Owns a horizontal row's geometry: the scroll ref, the chevrons' enabled/disabled state
// and the one-item paging step. The consumer is left with layout only.
const useHorizontalRow = () => {
  const rowRef = useRef<HTMLDivElement>(null);

  const [isAtStart, setIsAtStart] = useState(true);
  const [isAtEnd, setIsAtEnd] = useState(true);
  const [isScrollable, setIsScrollable] = useState(false);

  // A row whose items all fit never fires `scroll`, so it never learns that it has nothing to
  // page: a single-item collection rendered an enabled right chevron over content that was
  // already fully in front of the user. Measure the geometry directly -- on mount, on scroll,
  // and whenever the row's box changes (viewport / orientation change) -- instead of relying
  // on the scroll event alone.
  //
  // Both listeners live and die with this effect: `measure` has no consumer outside it,
  // so it does not need to be a component-scoped callback.
  useEffect(() => {
    const row = rowRef.current;
    if (!row) return;

    const measure = () => {
      const { scrollLeft, scrollWidth, clientWidth } = row;
      const scrollable = scrollWidth > clientWidth + 1;

      setIsScrollable(scrollable);
      setIsAtStart(!scrollable || scrollLeft <= 1);
      setIsAtEnd(!scrollable || scrollLeft + clientWidth >= scrollWidth - 1);
    };

    measure();

    row.addEventListener('scroll', measure, { passive: true });
    const observer = new ResizeObserver(measure);
    observer.observe(row);

    return () => {
      row.removeEventListener('scroll', measure);
      observer.disconnect();
    };
  }, []);

  const scrollByItem = useCallback((direction: -1 | 1) => {
    const row = rowRef.current;
    if (!row) return;

    // Clamping to the row's own range is what handles the tail: when less than a full
    // step remains, the press lands on the end instead of overshooting it.
    const end = row.scrollWidth - row.clientWidth;
    const next = Math.min(Math.max(row.scrollLeft + direction * getItemStepPx(row), 0), end);

    row.scrollTo({ left: next, behavior: 'smooth' });
  }, []);

  // Stable identities for the two chevrons -- a fresh closure per render buys nothing.
  const scrollPrev = useCallback(() => scrollByItem(-1), [scrollByItem]);
  const scrollNext = useCallback(() => scrollByItem(1), [scrollByItem]);

  return { rowRef, isScrollable, isAtStart, isAtEnd, scrollPrev, scrollNext };
};

export default useHorizontalRow;
